import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  Skeleton,
  Grid,
  Snackbar,
} from "@mui/material";
import {
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Insights as InsightsIcon,
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import TikTokIcon from "./icons/TikTokIcon";
import {
  MediaKitPlatformCard,
  isTiktokAudienceFormValid,
  TIKTOK_MANUAL_AGE_ORDER,
  type TiktokAudienceFormState,
} from "./MediaKitPlatformCard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import { Podcast } from "../services/api";
import {
  MediaKitMetricsWindowFields,
  normalizeMetricsWindow,
} from "./MediaKitMetricsWindowFields";
import { COMPANY_MEDIA_KITS_QUERY_KEY } from "../miContenido/useCompanyMediaKits";
import { MediaKitSettingsSection } from "./mediaKitSettings/MediaKitSettingsSection";
import { MediaKitMonthsPicker } from "./mediaKitSettings/MediaKitMonthsPicker";
import { MediaKitSharePanel } from "./mediaKitSettings/MediaKitSharePanel";
import { useLocale } from "../i18n/LocaleProvider";
import { withEnglishQuery } from "../i18n/locale";
import { MediaKitSaveBar } from "./mediaKitSettings/MediaKitSaveBar";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const MONTH_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

function monthName(index: number): string {
  return i18n.t(`mediaKit:months.${MONTH_KEYS[index]}`);
}

function getAvailableMonths(): { value: string; label: string }[] {
  const months: { value: string; label: string }[] = [];
  const now = new Date();
  const start = new Date(2026, 0, 1); // January 2026
  const d = new Date(start);
  while (d <= now) {
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${monthName(d.getMonth())} ${d.getFullYear()}`;
    months.push({ value, label });
    d.setMonth(d.getMonth() + 1);
  }
  return months;
}

function getDefaultSelectedMonths(): string[] {
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return [current];
}

export function formatSelectedMonths(months: string[]): string {
  if (!months || months.length === 0) return "";
  const sorted = [...months].sort();

  const byYear: Record<number, string[]> = {};
  for (const m of sorted) {
    const [y, mo] = m.split("-").map(Number);
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(monthName(mo - 1));
  }

  return Object.entries(byYear)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([year, names]) => {
      if (names.length === 1) return `${names[0]}, ${year}`;
      const last = names.pop();
      return `${names.join(", ")} ${i18n.t("mediaKit:and")} ${last}, ${year}`;
    })
    .join(" · ");
}

interface MediaKitSettingsProps {
  podcast: Podcast;
  companySlug?: string;
  /** Page already renders the podcast/media kit title (Mi contenido detail). */
  hideTitle?: boolean;
}

// Default enabled metrics structure: each platform has a general "enabled" flag + sub-params per section
const defaultEnabledMetrics = {
  youtube: {
    enabled: false,
    longForm: {
      showPdfVideoTable: false,
      consumption: {
        totalViews: true,
        avgViewsPerContent: true,
        totalInteractions: true,
        avgInteractions: true,
      },
      engagement: {
        engagementRate: true,
      },
      audience: {
        genderDistribution: true,
        ageDistribution: true,
      },
    },
    shorts: {
      consumption: {
        totalViews: false,
        avgViewsPerContent: false,
        totalInteractions: false,
        avgInteractions: false,
      },
    },
  },
  instagram: {
    enabled: false,
    reach: {
      totalReach: true,
      avgReach: true,
      totalViews: true,
      avgViews: true,
      totalInteractions: true,
      avgInteractions: true,
    },
    engagement: {
      engagementRate: true,
    },
    audience: {
      genderDistribution: true,
      ageDistribution: true,
    },
  },
  tiktok: {
    enabled: false,
    consumption: {
      totalViews: true,
      avgViewsPerContent: true,
      totalInteractions: true,
      avgInteractions: true,
    },
    engagement: {
      engagementRate: true,
    },
    audience: {
      genderDistribution: true,
      ageDistribution: true,
    },
  },
  podscope: {
    enabled: true,
    longForm: {
      consumption: {
        totalViews: true,
        avgViews: true,
        totalInteractions: true,
        avgInteractions: true,
      },
    },
    shorts: {
      consumption: {
        totalViews: false,
        avgViews: false,
        totalInteractions: false,
        avgInteractions: false,
      },
    },
    streaming: {
      avgViewers: true,
      peakViewers: true,
      chatMessages: true,
      chatMessagesAvg: true,
      uniqueChatUsersAvg: true,
    },
    brands: {
      paidSponsors: true,
    },
  },
};

function metricLabels() {
  const totalViews = i18n.t("mediaKit:metrics.totalViews");
  const avgViews = i18n.t("mediaKit:metrics.avgViews");
  const totalInteractions = i18n.t("mediaKit:metrics.totalInteractions");
  const avgInteractions = i18n.t("mediaKit:metrics.avgInteractions");
  const engagementRate = i18n.t("mediaKit:metrics.engagementRate");
  const genderDistribution = i18n.t("mediaKit:metrics.genderDistribution");
  const ageDistribution = i18n.t("mediaKit:metrics.ageDistribution");
  return {
    youtube: {
      longForm: {
        consumption: {
          totalViews,
          avgViewsPerContent: avgViews,
          totalInteractions,
          avgInteractions,
        },
        engagement: {
          engagementRate,
        },
        audience: {
          genderDistribution,
          ageDistribution,
        },
      },
      shorts: {
        consumption: {
          totalViews,
          avgViewsPerContent: avgViews,
          totalInteractions,
          avgInteractions,
        },
      },
    },
    instagram: {
      reach: {
        totalReach: i18n.t("mediaKit:metrics.reach"),
        avgReach: i18n.t("mediaKit:metrics.avgReach"),
        totalViews,
        avgViews,
        totalInteractions,
        avgInteractions,
      },
      engagement: {
        engagementRate,
      },
      audience: {
        genderDistribution,
        ageDistribution,
      },
    },
    tiktok: {
      consumption: {
        totalViews,
        avgViewsPerContent: avgViews,
        totalInteractions,
        avgInteractions,
      },
      engagement: {
        engagementRate,
      },
      audience: {
        genderDistribution,
        ageDistribution,
      },
    },
    podscope: {
      longForm: {
        consumption: {
          totalViews,
          avgViews,
          totalInteractions,
          avgInteractions,
        },
      },
      shorts: {
        consumption: {
          totalViews,
          avgViews,
          totalInteractions,
          avgInteractions,
        },
      },
      streaming: {
        avgViewers: i18n.t("mediaKit:metrics.avgViewers"),
        peakViewers: i18n.t("mediaKit:metrics.peakViewers"),
        chatMessages: i18n.t("mediaKit:metrics.chatMessages"),
        chatMessagesAvg: i18n.t("mediaKit:metrics.chatMessagesAvg"),
        uniqueChatUsersAvg: i18n.t("mediaKit:metrics.uniqueChatUsers"),
      },
      brands: {
        paidSponsors: i18n.t("mediaKit:metrics.paidSponsors"),
      },
    },
  };
}

function sectionLabels() {
  return {
    consumption: i18n.t("mediaKit:metrics.consumption"),
    reach: i18n.t("mediaKit:metrics.reach"),
    engagement: "Engagement",
    audience: i18n.t("mediaKit:metrics.audience"),
    streaming: "Streaming",
    brands: i18n.t("mediaKit:metrics.brands"),
  };
}

function createEmptyTiktokAudienceForm(): TiktokAudienceFormState {
  const ages: Record<string, string> = {};
  TIKTOK_MANUAL_AGE_ORDER.forEach((k) => {
    ages[k] = "";
  });
  return { male: "", female: "", unknown: "", ages };
}

function tiktokDemographicsToForm(
  d:
    | {
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      }
    | null
    | undefined,
): TiktokAudienceFormState {
  const base = createEmptyTiktokAudienceForm();
  if (!d) return base;
  for (const g of d.gender || []) {
    if (g.type === "male")
      base.male = g.percentage !== undefined ? String(g.percentage) : "";
    if (g.type === "female")
      base.female = g.percentage !== undefined ? String(g.percentage) : "";
    if (g.type === "unknown")
      base.unknown = g.percentage !== undefined ? String(g.percentage) : "";
  }
  for (const a of d.ageRanges || []) {
    if (Object.prototype.hasOwnProperty.call(base.ages, a.range)) {
      base.ages[a.range] =
        a.percentage !== undefined ? String(a.percentage) : "";
    }
  }
  return base;
}

function tiktokFormToPayload(form: TiktokAudienceFormState): {
  gender: Array<{ type: string; percentage: number }>;
  ageRanges: Array<{ range: string; percentage: number }>;
} {
  const gender: Array<{ type: string; percentage: number }> = [];
  const m = parseFloat(form.male);
  if (!Number.isNaN(m) && m > 0) gender.push({ type: "male", percentage: m });
  const f = parseFloat(form.female);
  if (!Number.isNaN(f) && f > 0)
    gender.push({ type: "female", percentage: f });
  const u = parseFloat(form.unknown);
  if (!Number.isNaN(u) && u > 0)
    gender.push({ type: "unknown", percentage: u });
  const ageRanges: Array<{ range: string; percentage: number }> = [];
  TIKTOK_MANUAL_AGE_ORDER.forEach((range) => {
    const v = parseFloat(form.ages[range] || "");
    if (!Number.isNaN(v) && v > 0) ageRanges.push({ range, percentage: v });
  });
  return { gender, ageRanges };
}

function metricsAllFalse(
  template: Record<string, boolean>,
): Record<string, boolean> {
  return Object.fromEntries(
    Object.keys(template).map((k) => [k, false]),
  ) as Record<string, boolean>;
}

function mergeLeafMetrics(
  defaults: Record<string, boolean>,
  saved: Record<string, boolean> | undefined,
): Record<string, boolean> {
  const out: Record<string, boolean> = { ...defaults };
  if (saved) {
    for (const k of Object.keys(defaults)) {
      if (saved[k] !== undefined) out[k] = Boolean(saved[k]);
    }
  }
  return out;
}

function hasAnyMetricEnabledTree(metrics: unknown): boolean {
  if (!metrics || typeof metrics !== "object" || Array.isArray(metrics))
    return false;
  return Object.entries(metrics as Record<string, unknown>).some(([k, v]) => {
    if (k === "enabled") return v === true;
    if (v && typeof v === "object" && !Array.isArray(v))
      return hasAnyMetricEnabledTree(v);
    return v === true;
  });
}

function mergeYoutubeEnabledMetrics(saved: unknown) {
  const d = defaultEnabledMetrics.youtube;
  if (!saved || typeof saved !== "object") {
    return {
      enabled: false,
      longForm: {
        consumption: metricsAllFalse(
          d.longForm.consumption as Record<string, boolean>,
        ),
        engagement: metricsAllFalse(
          d.longForm.engagement as Record<string, boolean>,
        ),
        audience: metricsAllFalse(
          d.longForm.audience as Record<string, boolean>,
        ),
        showPdfVideoTable: false,
      },
      shorts: {
        consumption: metricsAllFalse(
          d.shorts.consumption as Record<string, boolean>,
        ),
      },
    };
  }
  const s = saved as Record<string, unknown>;
  const hasNested = Boolean(s["longForm"] || s["shorts"]);
  const enabledVal =
    s["enabled"] !== undefined
      ? Boolean(s["enabled"])
      : hasAnyMetricEnabledTree(saved);

  if (hasNested) {
    const lf = s["longForm"] as Record<string, unknown> | undefined;
    const sh = s["shorts"] as Record<string, unknown> | undefined;
    return {
      enabled: enabledVal,
      longForm: {
        showContentCount: Boolean((lf as any)?.showContentCount),
        showPdfVideoTable: Boolean((lf as any)?.showPdfVideoTable),
        consumption: mergeLeafMetrics(
          d.longForm.consumption as Record<string, boolean>,
          lf?.["consumption"] as Record<string, boolean> | undefined,
        ),
        engagement: mergeLeafMetrics(
          d.longForm.engagement as Record<string, boolean>,
          lf?.["engagement"] as Record<string, boolean> | undefined,
        ),
        audience: mergeLeafMetrics(
          d.longForm.audience as Record<string, boolean>,
          lf?.["audience"] as Record<string, boolean> | undefined,
        ),
      },
      shorts: {
        showContentCount: Boolean((sh as any)?.showContentCount),
        consumption: mergeLeafMetrics(
          d.shorts.consumption as Record<string, boolean>,
          sh?.["consumption"] as Record<string, boolean> | undefined,
        ),
      },
    };
  }

  const cons = s["consumption"] as Record<string, boolean> | undefined;
  const eng = s["engagement"] as Record<string, boolean> | undefined;
  const aud = s["audience"] as Record<string, boolean> | undefined;
  return {
    enabled: enabledVal,
    longForm: {
      consumption: mergeLeafMetrics(
        d.longForm.consumption as Record<string, boolean>,
        cons,
      ),
      engagement: mergeLeafMetrics(
        d.longForm.engagement as Record<string, boolean>,
        eng,
      ),
      audience: mergeLeafMetrics(
        d.longForm.audience as Record<string, boolean>,
        aud,
      ),
    },
    shorts: {
      consumption: mergeLeafMetrics(
        d.shorts.consumption as Record<string, boolean>,
        cons,
      ),
    },
  };
}

function mergePodscopeEnabledMetrics(saved: unknown) {
  const d = defaultEnabledMetrics.podscope;
  if (!saved || typeof saved !== "object") {
    return {
      enabled: false,
      longForm: {
        consumption: metricsAllFalse(
          d.longForm.consumption as Record<string, boolean>,
        ),
      },
      shorts: {
        consumption: metricsAllFalse(
          d.shorts.consumption as Record<string, boolean>,
        ),
      },
      streaming: metricsAllFalse(d.streaming as Record<string, boolean>),
      brands: metricsAllFalse(d.brands as Record<string, boolean>),
    };
  }
  const s = saved as Record<string, unknown>;
  const hasNested = Boolean(s["longForm"] || s["shorts"]);
  const enabledVal =
    s["enabled"] !== undefined
      ? Boolean(s["enabled"])
      : hasAnyMetricEnabledTree(saved);

  if (hasNested) {
    const lf = s["longForm"] as Record<string, unknown> | undefined;
    const sh = s["shorts"] as Record<string, unknown> | undefined;
    const st = s["streaming"] as Record<string, unknown> | undefined;
    return {
      enabled: enabledVal,
      longForm: {
        showContentCount: Boolean(lf?.showContentCount),
        consumption: mergeLeafMetrics(
          d.longForm.consumption as Record<string, boolean>,
          lf?.["consumption"] as Record<string, boolean> | undefined,
        ),
      },
      shorts: {
        consumption: mergeLeafMetrics(
          d.shorts.consumption as Record<string, boolean>,
          sh?.["consumption"] as Record<string, boolean> | undefined,
        ),
      },
      streaming: {
        showContentCount: Boolean(st?.showContentCount),
        ...mergeLeafMetrics(
          d.streaming as Record<string, boolean>,
          st as Record<string, boolean> | undefined,
        ),
      },
      brands: mergeLeafMetrics(
        d.brands as Record<string, boolean>,
        s["brands"] as Record<string, boolean> | undefined,
      ),
    };
  }

  const cons = s["consumption"] as Record<string, boolean> | undefined;
  const st = s["streaming"] as Record<string, unknown> | undefined;
  return {
    enabled: enabledVal,
    longForm: {
      consumption: mergeLeafMetrics(
        d.longForm.consumption as Record<string, boolean>,
        cons,
      ),
    },
    shorts: {
      consumption: mergeLeafMetrics(
        d.shorts.consumption as Record<string, boolean>,
        cons,
      ),
    },
    streaming: {
      showContentCount: Boolean(st?.showContentCount),
      ...mergeLeafMetrics(
        d.streaming as Record<string, boolean>,
        st as Record<string, boolean> | undefined,
      ),
    },
    brands: mergeLeafMetrics(
      d.brands as Record<string, boolean>,
      s["brands"] as Record<string, boolean> | undefined,
    ),
  };
}

function mapMetricTemplateEnabled(
  template: unknown,
  enabled: boolean,
): Record<string, unknown> {
  if (!template || typeof template !== "object" || Array.isArray(template)) {
    return {};
  }
  const t = template as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(t)) {
    if (k === "enabled") {
      out[k] = enabled;
      continue;
    }
    if (typeof v === "boolean") {
      out[k] = v;
      continue;
    }
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const child = v as Record<string, unknown>;
      const values = Object.values(child);
      const isLeafMetrics =
        values.length > 0 && values.every((x) => typeof x === "boolean");
      if (isLeafMetrics) {
        out[k] = Object.fromEntries(
          Object.keys(child).map((key) => [key, enabled]),
        );
      } else {
        out[k] = mapMetricTemplateEnabled(v, enabled);
      }
    }
  }
  return out;
}

function updateMetricAtPath(
  branch: Record<string, unknown>,
  path: string[],
  metric: string,
  value: boolean,
): Record<string, unknown> {
  if (path.length === 0) {
    return { ...branch, [metric]: value };
  }
  const [head, ...rest] = path;
  const prevChild = (branch[head] as Record<string, unknown>) || {};
  return {
    ...branch,
    [head]: updateMetricAtPath(prevChild, rest, metric, value),
  };
}

// Merge saved metrics with defaults for platforms that exist in saved data.
// Platforms not present in savedMetrics are included with all metrics disabled.
function mergeWithDefaults(savedMetrics: any) {
  if (!savedMetrics) return defaultEnabledMetrics;

  const mergePlatform = (platform: string, defaults: any, saved: any) => {
    if (!saved) {
      const disabled: any = { enabled: false };
      for (const [sectionKey, sectionMetrics] of Object.entries(defaults)) {
        if (sectionKey === "enabled") continue;
        disabled[sectionKey] = {};
        for (const metricKey of Object.keys(sectionMetrics as object)) {
          disabled[sectionKey][metricKey] = false;
        }
      }
      return disabled;
    }
    const merged: any = {};
    // Preserve showContentCount from saved data (not part of metric defaults)
    if (saved.showContentCount !== undefined) {
      merged.showContentCount = saved.showContentCount;
    }
    for (const [sectionKey, sectionDefaults] of Object.entries(defaults)) {
      if (sectionKey === "enabled") {
        merged.enabled =
          saved.enabled !== undefined
            ? saved.enabled
            : hasAnyMetricEnabledTree(saved);
        continue;
      }
      merged[sectionKey] = {
        ...(sectionDefaults as object),
        ...saved[sectionKey],
      };
    }
    return merged;
  };

  return {
    youtube: mergeYoutubeEnabledMetrics(savedMetrics.youtube),
    instagram: mergePlatform(
      "instagram",
      defaultEnabledMetrics.instagram,
      savedMetrics.instagram,
    ),
    tiktok: mergePlatform(
      "tiktok",
      defaultEnabledMetrics.tiktok,
      savedMetrics.tiktok,
    ),
    podscope: mergePodscopeEnabledMetrics(savedMetrics.podscope),
  };
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map(
        (k) =>
          `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`,
      )
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

function normalizeSavedMediaKit(saved: any) {
  return {
    ...saved,
    selectedMonths: saved?.selectedMonths?.length
      ? [...saved.selectedMonths].sort()
      : getDefaultSelectedMonths(),
    enabledMetrics: mergeWithDefaults(saved?.enabledMetrics),
    metricsWindow: normalizeMetricsWindow(saved?.metricsWindow),
  };
}

function countEnabledLeafMetrics(branch: unknown): {
  on: number;
  total: number;
} {
  let on = 0;
  let total = 0;
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (k === "enabled" || k === "showContentCount" || k === "showPdfVideoTable")
        continue;
      if (typeof v === "boolean") {
        total += 1;
        if (v) on += 1;
      } else walk(v);
    }
  };
  walk(branch);
  return { on, total };
}

const MediaKitSettings: React.FC<MediaKitSettingsProps> = ({
  podcast,
  companySlug,
  hideTitle,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("mediaKit");
  const { locale } = useLocale();
  const [youtubeExpanded, setYoutubeExpanded] = useState(false);
  const [instagramExpanded, setInstagramExpanded] = useState(false);
  const [tiktokExpanded, setTiktokExpanded] = useState(false);
  const [podscopeExpanded, setPodscopeExpanded] = useState(false);
  const [savedToastOpen, setSavedToastOpen] = useState(false);

  const {
    data: settingsData,
    isLoading,
    isError: settingsError,
    error: settingsQueryError,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ["media-kit-settings", podcast.slug],
    queryFn: () => apiService.getPodcastMediaKitSettings(podcast.slug),
    enabled: !!podcast.slug,
    retry: 1,
  });

  const { data: socialAccounts } = useQuery({
    queryKey: ["social-accounts", podcast._id],
    queryFn: () => apiService.getSocialAccountsByPodcast(podcast._id),
    enabled: !!podcast._id,
  });

  const connectedPlatforms = React.useMemo(() => {
    const platforms = new Set<string>();
    socialAccounts?.forEach((acc) => {
      if (acc.platform === "meta") platforms.add("instagram");
      else platforms.add(acc.platform);
    });
    return platforms;
  }, [socialAccounts]);

  const [tiktokAudienceForm, setTiktokAudienceForm] = useState(
    createEmptyTiktokAudienceForm(),
  );

  const updateMutation = useMutation({
    mutationFn: (payload: {
      mediaKit: any;
      tiktokAudienceDemographics: ReturnType<typeof tiktokFormToPayload>;
    }) => {
      return apiService.updatePodcastMediaKitSettings(
        podcast.slug,
        payload,
      );
    },
    onSuccess: () => {
      setSavedToastOpen(true);
      queryClient.invalidateQueries({
        queryKey: ["media-kit-settings", podcast.slug],
      });
      queryClient.invalidateQueries({
        queryKey: ["media-kit-linked-programs", podcast.slug],
      });
      queryClient.invalidateQueries({
        queryKey: [COMPANY_MEDIA_KITS_QUERY_KEY],
      });
    },
    onError: (error: any) => {
      console.error("❌ Error saving media kit settings:", error);
    },
  });

  const baseline = React.useMemo(() => {
    if (!settingsData) return null;
    return {
      mediaKit: normalizeSavedMediaKit(settingsData.mediaKit),
      tiktokAudienceForm: tiktokDemographicsToForm(
        settingsData.tiktokAudienceDemographics,
      ),
    };
  }, [settingsData]);

  const [formData, setFormData] = useState<any>(() =>
    normalizeSavedMediaKit(undefined),
  );
  const [hydrated, setHydrated] = useState(false);

  React.useEffect(() => {
    if (!baseline) return;
    setFormData(baseline.mediaKit);
    setTiktokAudienceForm(baseline.tiktokAudienceForm);
    setHydrated(true);
  }, [baseline]);

  const isDirty = React.useMemo(() => {
    if (!hydrated || !baseline) return false;
    const comparable = (mk: any) => ({
      ...mk,
      selectedMonths: [...(mk?.selectedMonths ?? [])].sort(),
      metricsWindow: normalizeMetricsWindow(mk?.metricsWindow),
    });
    const current = {
      mediaKit: comparable(formData),
      tiktokAudienceForm,
    };
    const saved = {
      mediaKit: comparable(baseline.mediaKit),
      tiktokAudienceForm: baseline.tiktokAudienceForm,
    };
    return stableStringify(current) !== stableStringify(saved);
  }, [hydrated, baseline, formData, tiktokAudienceForm]);

  React.useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const handleDiscard = () => {
    if (!baseline) return;
    setFormData(baseline.mediaKit);
    setTiktokAudienceForm(baseline.tiktokAudienceForm);
    updateMutation.reset();
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleContentCountToggle = (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    value: boolean,
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      enabledMetrics: {
        ...prev.enabledMetrics,
        [platform]: {
          ...(prev.enabledMetrics?.[platform] || {}),
          showContentCount: value,
        },
      },
    }));
  };

  const handleMetricToggle = (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    path: string[],
    metric: string,
    value: boolean,
  ) => {
    setFormData((prev: any) => {
      const branch = (prev.enabledMetrics?.[platform] || {}) as Record<
        string,
        unknown
      >;
      return {
        ...prev,
        enabledMetrics: {
          ...prev.enabledMetrics,
          [platform]: updateMetricAtPath(branch, path, metric, value),
        },
      };
    });
  };

  // Check if entire platform section is enabled: explicit enabled flag, or (backward compat) any sub-metric on
  const isPlatformEnabled = (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
  ) => {
    const metrics = formData.enabledMetrics?.[platform];
    if (!metrics) return false;
    if (metrics.enabled === false) return false;
    if (metrics.enabled === true) return true;
    return hasAnyMetricEnabledTree(metrics);
  };

  const platformRequiresAccount = (platform: string): boolean =>
    ["youtube", "instagram", "tiktok"].includes(platform);

  // Toggle entire platform: set general "enabled" flag + all sub-params for that section
  const handlePlatformToggle = (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    enabled: boolean,
  ) => {
    if (
      enabled &&
      platformRequiresAccount(platform) &&
      !connectedPlatforms.has(platform)
    ) {
      return;
    }

    const platformDefaults = defaultEnabledMetrics[platform];
    const newMetrics: any =
      platform === "youtube" || platform === "podscope"
        ? mapMetricTemplateEnabled(platformDefaults, enabled)
        : (() => {
            const built: any = { enabled };
            for (const [sectionKey, sectionMetrics] of Object.entries(
              platformDefaults,
            )) {
              if (sectionKey === "enabled") continue;
              built[sectionKey] = {};
              for (const metricKey of Object.keys(sectionMetrics as object)) {
                built[sectionKey][metricKey] = enabled;
              }
            }
            return built;
          })();
    setFormData((prev: any) => ({
      ...prev,
      enabledMetrics: {
        ...prev.enabledMetrics,
        [platform]: newMetrics,
      },
    }));
  };

  const handleSave = () => {
    if (!isTiktokAudienceFormValid(tiktokAudienceForm)) {
      return;
    }
    const mediaKitData = {
      ...formData,
      selectedMonths: formData.selectedMonths?.length
        ? formData.selectedMonths
        : getDefaultSelectedMonths(),
      // Persist full merged metrics so TikTok manual audience toggles default like IG/YT.
      enabledMetrics: mergeWithDefaults(formData.enabledMetrics),
      metricsWindow: normalizeMetricsWindow(formData.metricsWindow),
    };
    updateMutation.mutate({
      mediaKit: mediaKitData,
      tiktokAudienceDemographics: tiktokFormToPayload(tiktokAudienceForm),
    });
  };

  const mediaKitUrl = withEnglishQuery(
    companySlug
      ? `${window.location.origin}/${companySlug}/${podcast.slug}`
      : `${window.location.origin}/${podcast.slug}`,
    locale,
  );

  if (isLoading) {
    return (
      <>
        {!hideTitle && (
          <Box className="flex items-center gap-2 mb-6">
            <Skeleton variant="text" width={200} height={48} />
          </Box>
        )}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={3}>
              <Skeleton variant="rounded" height={160} />
              <Skeleton variant="rounded" height={160} />
              <Skeleton variant="rounded" height={280} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rounded" height={320} />
          </Grid>
        </Grid>
      </>
    );
  }

  if (settingsError) {
    const status = (settingsQueryError as { response?: { status?: number } })
      ?.response?.status;
    const message =
      status === 403
        ? t("forbidden")
        : status === 401
          ? t("sessionExpired")
          : t("loadFailed");

    return (
      <>
        {!hideTitle && (
          <Box className="flex items-center gap-2 mb-6">
            <Typography variant="h2">Media Kit</Typography>
          </Box>
        )}
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => refetchSettings()}
            >
              {t("retry")}
            </Button>
          }
        >
          {message}
        </Alert>
      </>
    );
  }

  const platformCardSharedProps = {
    connectAccountHref: podcast.slug
      ? `/settings/podcasts/${encodeURIComponent(podcast.slug)}`
      : undefined,
    formData,
    setFormData,
    connectedPlatforms,
    isPlatformEnabled,
    platformRequiresAccount,
    handlePlatformToggle,
    handleContentCountToggle,
    handleMetricToggle,
    tiktokAudienceForm,
    setTiktokAudienceForm,
    metricLabels: metricLabels(),
    sectionLabels: sectionLabels(),
  };

  const platformSummary = (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
  ): string => {
    if (!isPlatformEnabled(platform)) return t("off");
    const { on, total } = countEnabledLeafMetrics(
      formData.enabledMetrics?.[platform],
    );
    return t("metricsCount", { on, total });
  };

  const metricsWindow = normalizeMetricsWindow(formData.metricsWindow);
  const tiktokValid = isTiktokAudienceFormValid(tiktokAudienceForm);
  const enabledPlatformsCount = (
    ["youtube", "instagram", "tiktok", "podscope"] as const
  ).filter((p) => isPlatformEnabled(p)).length;

  const saveBarMessage = !tiktokValid
    ? t("tiktokSum")
    : updateMutation.isError
      ? t("saveError")
      : null;

  return (
    <>
      {!hideTitle && (
        <Box className="flex items-center gap-2 mb-6">
          <Typography variant="h2">Media Kit</Typography>
        </Box>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 1, md: 2 } }}>
          <Box sx={{ position: { md: "sticky" }, top: { md: 24 } }}>
            <MediaKitSharePanel
              podcastSlug={podcast.slug || ""}
              companySlug={companySlug}
              mediaKitUrl={mediaKitUrl}
              enabled={Boolean(formData.enabled)}
              savedEnabled={Boolean(baseline?.mediaKit?.enabled)}
              onEnabledChange={(v) => handleChange("enabled", v)}
              selectedMonths={
                baseline?.mediaKit?.selectedMonths ?? getDefaultSelectedMonths()
              }
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }} sx={{ order: { xs: 2, md: 1 } }}>
          <Stack spacing={3}>
            {!formData.enabled && (
              <Alert severity="info">
                {t("disabledInfo")}
              </Alert>
            )}

            <MediaKitSettingsSection
              icon={<ScheduleIcon fontSize="small" />}
              title={t("windowTitle")}
              description={t("windowDescription")}
            >
              <MediaKitMetricsWindowFields
                value={metricsWindow}
                onChange={(next) => handleChange("metricsWindow", next)}
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                {metricsWindow.enabled
                  ? t("windowCompare", {
                      longForm: metricsWindow.longFormDays,
                      shorts: metricsWindow.shortsDays,
                    })
                  : t("windowCumulative")}
              </Typography>
            </MediaKitSettingsSection>

            <MediaKitSettingsSection
              icon={<CalendarMonthIcon fontSize="small" />}
              title={t("visibleMonths")}
              description={t("visibleMonthsDescription")}
            >
              <MediaKitMonthsPicker
                options={getAvailableMonths()}
                value={formData.selectedMonths || []}
                onChange={(next) => handleChange("selectedMonths", next)}
              />
            </MediaKitSettingsSection>

            <MediaKitSettingsSection
              icon={<TuneIcon fontSize="small" />}
              title={t("metricsByPlatform")}
              description={t("platformsActive", {
                count: enabledPlatformsCount,
              })}
            >
              <MediaKitPlatformCard
                platform="youtube"
                icon={<YouTubeIcon />}
                expanded={youtubeExpanded}
                setExpanded={setYoutubeExpanded}
                summary={platformSummary("youtube")}
                {...platformCardSharedProps}
              />
              <MediaKitPlatformCard
                platform="instagram"
                icon={<InstagramIcon />}
                expanded={instagramExpanded}
                setExpanded={setInstagramExpanded}
                summary={platformSummary("instagram")}
                {...platformCardSharedProps}
              />
              <MediaKitPlatformCard
                platform="tiktok"
                icon={<TikTokIcon />}
                expanded={tiktokExpanded}
                setExpanded={setTiktokExpanded}
                summary={platformSummary("tiktok")}
                {...platformCardSharedProps}
              />
              <MediaKitPlatformCard
                platform="podscope"
                icon={<InsightsIcon />}
                expanded={podscopeExpanded}
                setExpanded={setPodscopeExpanded}
                summary={platformSummary("podscope")}
                {...platformCardSharedProps}
              />
              {!tiktokValid && (
                <Alert severity="warning">
                  {t("tiktokSumDetail")}
                </Alert>
              )}
            </MediaKitSettingsSection>

            <MediaKitSaveBar
              visible={isDirty}
              saving={updateMutation.isPending}
              canSave={tiktokValid}
              message={saveBarMessage}
              messageSeverity={tiktokValid ? "error" : "warning"}
              onDiscard={handleDiscard}
              onSave={handleSave}
            />
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={savedToastOpen}
        autoHideDuration={3000}
        onClose={() => setSavedToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSavedToastOpen(false)}
        >
          {t("saved")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MediaKitSettings;
